import type {
  AgentConfig,
  Message,
  MessageModality,
  ErrorType,
  PendingApproval,
} from "../../shared/types";
import {
  EventType,
  type StreamEvent,
  type TextMessageStartEvent,
  type ToolCallStartEvent,
  type ToolCallEndEvent,
} from "./agui-protocol";
import { isValidGuid } from "../../shared/validation";
import { TOOL_APPROVAL_REQUESTED } from "./resident-agent-protocol";

export interface ApiError extends Error {
  errorType: ErrorType;
  statusCode?: number;
  retryable: boolean;
  userMessage: string;
  pendingApprovals?: PendingApproval[];
}

interface AgUiEventCallbacks {
  onThreadId: (threadId: string) => void;
  onDelta: (delta: string) => void;
  onMessageStart?: (event: TextMessageStartEvent) => void;
  onError?: (message: string) => void;
  onStatus?: (event: ToolCallStartEvent | ToolCallEndEvent) => void;
  onToolApprovalRequested?: (approval: PendingApproval) => void;
  onMessageId?: (messageId: string) => void;
}

const NETWORK_ERROR_MESSAGE =
  "I'm having trouble connecting right now. Please check your internet connection and try again.";

function throwNetworkError(cause?: unknown): never {
  if ((cause as ApiError)?.errorType) {
    throw cause;
  }
  throw createApiError("network", NETWORK_ERROR_MESSAGE, undefined, true);
}

function createApiError(
  errorType: ErrorType,
  message: string,
  statusCode?: number,
  retryable: boolean = true,
): ApiError {
  const error = new Error(message) as ApiError;
  error.errorType = errorType;
  error.statusCode = statusCode;
  error.retryable = retryable;
  error.userMessage = message;
  return error;
}

function getErrorMessage(
  statusCode: number,
  statusText: string,
): {
  errorType: ErrorType;
  message: string;
  retryable: boolean;
} {
  switch (statusCode) {
    case 429:
      return {
        errorType: "rateLimit",
        message:
          "I'm receiving too many requests right now. Please wait a moment and try again.",
        retryable: true,
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        errorType: "serverError",
        message:
          "Something went wrong on my end. Please try again in a moment.",
        retryable: true,
      };
    case 400:
      return {
        errorType: "clientError",
        message: "I couldn't understand that request. Please try rephrasing.",
        retryable: false,
      };
    case 401:
    case 403:
      return {
        errorType: "clientError",
        message: "I don't have permission to process that request.",
        retryable: false,
      };
    default:
      return {
        errorType: "unknown",
        message: "An unexpected error occurred. Please try again.",
        retryable: true,
      };
  }
}

function handleAgUiEvent(
  event: StreamEvent,
  callbacks: AgUiEventCallbacks,
): void {
  switch (event.type) {
    case EventType.RUN_STARTED:
      if ("threadId" in event && event.threadId) {
        callbacks.onThreadId(event.threadId);
      }
      break;

    case EventType.TEXT_MESSAGE_START:
      if ("conversationId" in event && event.conversationId) {
        callbacks.onThreadId(event.conversationId);
      }
      callbacks.onMessageStart?.(event);
      if (
        "messageId" in event &&
        event.messageId &&
        isValidGuid(event.messageId)
      ) {
        callbacks.onMessageId?.(event.messageId);
      }
      break;

    case EventType.TEXT_MESSAGE_CONTENT:
      if ("delta" in event && event.delta) {
        callbacks.onDelta(event.delta);
      }
      break;

    case EventType.TEXT_MESSAGE_END:
      break;

    case EventType.RUN_FINISHED:
      break;

    case EventType.RUN_ERROR:
      if ("message" in event) {
        callbacks.onError?.(event.message || "Unknown error occurred");
      }
      break;

    case EventType.TOOL_CALL_START:
    case EventType.TOOL_CALL_END:
      callbacks.onStatus?.(event);
      break;

    case EventType.REASONING_START:
    case EventType.REASONING_END:
      break;

    case TOOL_APPROVAL_REQUESTED:
      if (event.conversationId) {
        callbacks.onThreadId(event.conversationId);
      }
      callbacks.onToolApprovalRequested?.({
        toolApprovalId: event.toolApprovalId,
        conversationId: event.conversationId,
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        args: event.args,
        clientRequirements: event.clientRequirements,
      });
      break;

    default:
      if (import.meta.env.DEV) {
        console.warn("Unknown AG-UI event type:", event);
      }
  }
}

export class ApiClient {
  private baseUrl: string;
  private agentId: string;

  constructor(baseUrl: string, agentId: string) {
    this.baseUrl = baseUrl;
    this.agentId = agentId;
  }

  async getAgentConfig(): Promise<AgentConfig> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/v1/public/agents/${this.agentId}/configuration`,
      );

      if (!response.ok) {
        const { errorType, message, retryable } = getErrorMessage(
          response.status,
          response.statusText,
        );
        throw createApiError(errorType, message, response.status, retryable);
      }

      const data = await response.json();
      return {
        ...data,
        voiceEnabled: data.voiceConfig?.isEnabled === true,
      };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw createApiError(
          "network",
          "I'm having trouble connecting right now. Please check your internet connection and try again.",
          undefined,
          true,
        );
      }
      if ((error as ApiError).errorType) {
        throw error;
      }
      throw createApiError(
        "unknown",
        "An unexpected error occurred. Please try again.",
        undefined,
        true,
      );
    }
  }

  private async parseSSEStream(
    response: Response,
    callbacks: AgUiEventCallbacks,
    initialConversationId: string | null,
  ): Promise<{
    conversationId: string;
    fullContent: string;
    contentFormat?: Message["contentFormat"];
    messageId?: string;
  }> {
    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullContent = "";
    let conversationIdFromStream = initialConversationId;
    let messageIdFromStream: string | undefined;
    let contentFormatFromStream: Message["contentFormat"];

    const streamingError = createApiError(
      "serverError",
      "Something went wrong while processing your request. Please try again.",
      500,
      true,
    );

    const eventCallbacks: AgUiEventCallbacks = {
      ...callbacks,
      onThreadId: (threadId: string) => {
        conversationIdFromStream = threadId;
        callbacks.onThreadId(threadId);
      },
      onDelta: (delta: string) => {
        fullContent += delta;
        callbacks.onDelta(delta);
      },
      onMessageStart: (event) => {
        if (event.contentFormat) {
          contentFormatFromStream = event.contentFormat;
        }
        callbacks.onMessageStart?.(event);
      },
      onError: () => {
        throw streamingError;
      },
      onMessageId: (messageId: string) => {
        messageIdFromStream = messageId;
        callbacks.onMessageId?.(messageId);
      },
    };

    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();

            if (data === "[DONE]") {
              continue;
            }

            try {
              const event = JSON.parse(data) as StreamEvent;
              handleAgUiEvent(event, eventCallbacks);
            } catch (e) {
              if (e instanceof SyntaxError) {
                if (import.meta.env.DEV) {
                  console.error("Failed to parse AG-UI event:", data, e);
                }
                throw streamingError;
              } else {
                throw e;
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return {
      conversationId: conversationIdFromStream || "",
      fullContent,
      contentFormat: contentFormatFromStream,
      messageId: messageIdFromStream,
    };
  }

  async speakText(
    text: string,
    signal?: AbortSignal,
    messageId?: string,
  ): Promise<ArrayBuffer> {
    const body: { text: string; messageId?: string } = { text };
    if (messageId && isValidGuid(messageId)) {
      body.messageId = messageId;
    }
    const response = await fetch(
      `${this.baseUrl}/api/v1/public/agents/${this.agentId}/voice/speak`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      },
    );

    if (!response.ok) {
      const { errorType, message, retryable } = getErrorMessage(
        response.status,
        response.statusText,
      );
      throw createApiError(errorType, message, response.status, retryable);
    }

    return response.arrayBuffer();
  }

  async sendMessage(
    message: string,
    conversationId: string | null,
    onChunk?: (chunk: string) => void,
    onStatus?: (event: ToolCallStartEvent | ToolCallEndEvent) => void,
    onToolApprovalRequested?: (approval: PendingApproval) => void,
    onThreadId?: (threadId: string) => void,
    onMessageStart?: (event: TextMessageStartEvent) => void,
    modality?: MessageModality,
  ): Promise<{
    conversationId: string;
    message: Message;
    messageId?: string;
  }> {
    if (conversationId && import.meta.env.DEV) {
      console.debug("[widget] sendMessage conversationId:", conversationId);
    }

    const requestBody: {
      message: string;
      conversationId?: string;
      modality?: string;
    } = {
      message,
    };
    if (conversationId && isValidGuid(conversationId)) {
      requestBody.conversationId = conversationId;
    }
    if (modality) {
      requestBody.modality = modality;
    }

    let response: Response;
    try {
      response = await fetch(
        `${this.baseUrl}/api/v1/public/agents/${this.agentId}/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );
    } catch (error) {
      throwNetworkError(error);
    }

    if (!response.ok) {
      if (response.status === 409) {
        const body = await response.json().catch(() => ({}));
        const err = createApiError(
          "clientError",
          "Pending approvals must be resolved before sending a message.",
          409,
          false,
        );
        err.pendingApprovals = Array.isArray(body?.pendingApprovals)
          ? body.pendingApprovals
          : [];
        throw err;
      }
      const {
        errorType,
        message: errMsg,
        retryable,
      } = getErrorMessage(response.status, response.statusText);
      throw createApiError(errorType, errMsg, response.status, retryable);
    }

    const {
      conversationId: resolvedConversationId,
      fullContent,
      contentFormat,
      messageId,
    } = await this.parseSSEStream(
      response,
      {
        onThreadId: (threadId) => onThreadId?.(threadId),
        onDelta: (delta) => onChunk?.(delta),
        onMessageStart,
        onStatus,
        onToolApprovalRequested,
      },
      conversationId,
    );

    const assistantMessage: Message = {
      id: Date.now().toString(),
      role: "assistant",
      content: fullContent,
      contentFormat,
      timestamp: Date.now(),
    };

    return {
      conversationId: resolvedConversationId,
      message: assistantMessage,
      messageId,
    };
  }

  async sendApproval(
    conversationId: string,
    toolApprovalId: string,
    decision: "approved" | "denied",
    onChunk?: (chunk: string) => void,
    onStatus?: (event: ToolCallStartEvent | ToolCallEndEvent) => void,
    onToolApprovalRequested?: (approval: PendingApproval) => void,
    onThreadId?: (threadId: string) => void,
    onMessageStart?: (event: TextMessageStartEvent) => void,
    clientArtifacts?: Record<string, string>,
    toolResult?: string,
  ): Promise<{
    conversationId: string;
    message: Message;
    messageId?: string;
  }> {
    let response: Response;
    try {
      response = await fetch(
        `${this.baseUrl}/api/v1/public/agents/${this.agentId}/conversations/${conversationId}/approvals/${toolApprovalId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ decision, clientArtifacts, toolResult }),
        },
      );
    } catch (error) {
      throwNetworkError(error);
    }

    if (!response.ok) {
      if (response.status === 409) {
        const body = await response.json().catch(() => ({}));
        throw createApiError(
          "clientError",
          body?.error ?? "This approval is no longer pending.",
          409,
          false,
        );
      }
      if (response.status === 400) {
        const body = await response.json().catch(() => ({}));
        const { errorType, message, retryable } = getErrorMessage(
          400,
          response.statusText,
        );
        const msg = body?.detail ?? body?.error ?? body?.message ?? message;
        throw createApiError(errorType, msg, 400, retryable);
      }
      const {
        errorType,
        message: errMsg,
        retryable,
      } = getErrorMessage(response.status, response.statusText);
      throw createApiError(errorType, errMsg, response.status, retryable);
    }

    const {
      conversationId: resolvedConversationId,
      fullContent,
      contentFormat,
      messageId,
    } = await this.parseSSEStream(
      response,
      {
        onThreadId: (threadId) => onThreadId?.(threadId),
        onDelta: (delta) => onChunk?.(delta),
        onMessageStart,
        onStatus,
        onToolApprovalRequested,
      },
      conversationId,
    );

    const assistantMessage: Message = {
      id: Date.now().toString(),
      role: "assistant",
      content: fullContent,
      contentFormat,
      timestamp: Date.now(),
    };

    return {
      conversationId: resolvedConversationId,
      message: assistantMessage,
      messageId,
    };
  }

  async transcribeAudio(
    audioBlob: Blob,
    mimeType: string,
    language?: string,
  ): Promise<{ transcript: string; language?: string }> {
    const baseType = mimeType.split(";")[0].trim();
    const extensionMap: Record<string, string> = {
      "audio/webm": "webm",
      "audio/mp4": "mp4",
      "audio/ogg": "ogg",
    };
    const ext = extensionMap[baseType] ?? "webm";
    const filename = `recording.${ext}`;

    const formData = new FormData();
    formData.append("audio", audioBlob, filename);
    if (language) {
      formData.append("language", language);
    }

    const response = await fetch(
      `${this.baseUrl}/api/v1/public/agents/${this.agentId}/voice/transcribe`,
      {
        method: "POST",
        body: formData,
      },
    );

    if (!response.ok) {
      if (response.status === 422) {
        throw new Error("NO_SPEECH_DETECTED");
      }
      const { errorType, message, retryable } = getErrorMessage(
        response.status,
        response.statusText,
      );
      throw createApiError(errorType, message, response.status, retryable);
    }

    const data = await response.json();
    return {
      transcript: data.transcript ?? "",
      language: data.language ?? undefined,
    };
  }
}
